import React, { useEffect, useMemo, useRef, useState } from 'react'
import classnames from 'classnames'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import ArrowDown from '@Images/languages_dropdown_arrow.svg'

import './FormDropdown.scss'

const LISTENER_CLASS_PREFIX = 'FormDropdown__listener-'

const generateListenerClass = () => `${LISTENER_CLASS_PREFIX}${Math.random().toString(36).slice(2, 10)}`

export interface FormDropdownOption<T> {
  readonly value: T
  readonly label: string
  readonly description?: string
}

export interface FormDropdownProps<T> {
  readonly value: T | null
  readonly placeholder: string
  readonly options: ReadonlyArray<FormDropdownOption<T>>
  readonly disabled?: boolean
  readonly allowSearch?: boolean
  readonly allowClear?: boolean
  readonly searchPlaceholder?: string
  readonly clearLabel?: string
  readonly noResultsLabel?: string
  readonly className?: string
  readonly menuClassName?: string
  readonly getValueLabel?: (value: T) => string
  readonly isOptionEqual?: (optionValue: T, currentValue: T) => boolean
  readonly allowCreateOption?: boolean
  readonly renderCreateOptionLabel?: (input: string) => string
  readonly onCreateOption?: (input: string) => T
  onChange(nextValue: T | null): void
}

const FormDropdown = <T,>(props: FormDropdownProps<T>) => {
  const {
    value,
    placeholder,
    options,
    disabled,
    allowSearch,
    allowClear,
    searchPlaceholder,
    clearLabel,
    noResultsLabel,
    className,
    menuClassName,
    getValueLabel,
    isOptionEqual,
    allowCreateOption,
    renderCreateOptionLabel,
    onCreateOption,
    onChange,
  } = props

  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')

  const listenerClassRef = useRef<string>(generateListenerClass())
  const listenerClass = listenerClassRef.current

  const effectiveIsOptionEqual = useMemo(() => {
    return isOptionEqual ?? ((optionValue: T, currentValue: T) => optionValue === currentValue)
  }, [isOptionEqual])

  const selectedOption = useMemo(() => {
    if (value === null) { return null }
    return options.find(option => effectiveIsOptionEqual(option.value, value)) ?? null
  }, [value, options, effectiveIsOptionEqual])

  const trimmedFilter = filter.trim()
  const normalizedFilter = trimmedFilter.toLowerCase()
  const shouldFilter = Boolean(allowSearch && normalizedFilter)
  const filteredOptions = useMemo(() => {
    if (!shouldFilter) { return options }

    return options.filter(option => {
      const labelMatch = option.label.toLowerCase().includes(normalizedFilter)
      const descriptionMatch = option.description ? option.description.toLowerCase().includes(normalizedFilter) : false
      return labelMatch || descriptionMatch
    })
  }, [options, shouldFilter, normalizedFilter])
  const isEmpty = !filteredOptions.length

  const hasMatchingLabel = useMemo(() => {
    if (!normalizedFilter) { return false }
    return options.some(option => option.label.toLowerCase() === normalizedFilter)
  }, [options, normalizedFilter])

  const shouldShowCreateOption = Boolean(
    allowCreateOption &&
    allowSearch &&
    onCreateOption &&
    normalizedFilter &&
    !hasMatchingLabel
  )

  const displayLabel = selectedOption
    ? selectedOption.label
    : value !== null && getValueLabel
      ? getValueLabel(value)
      : ''

  const toggleDropdown = () => {
    if (disabled) { return }

    setOpen(prev => {
      const nextOpen = !prev
      if (!nextOpen) {
        setFilter('')
      }
      return nextOpen
    })
  }

  const closeDropdown = () => {
    setOpen(false)
    setFilter('')
  }

  const handleSelect = (nextValue: T | null) => {
    onChange(nextValue)
    closeDropdown()
  }

  const resetSelect = () => {
    handleSelect(null)
  }

  const handleCreateOption = () => {
    if (!onCreateOption || !trimmedFilter) { return }

    const createdValue = onCreateOption(trimmedFilter)
    onChange(createdValue)
    closeDropdown()
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value)
  }

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  useClickOutsideListener(listenerClass, setOpen, open)

  const searchInputPlaceholder = searchPlaceholder ?? 'Search'
  const emptyLabel = noResultsLabel ?? 'No results found'
  const clearOptionLabel = clearLabel ?? 'Clear selection'

  useEffect(() => {
    if (!open) {
      setFilter('')
    }
  }, [open])

  const filteredOptionsElements = filteredOptions.map(option => {
    return (
      <li
        key={option.label}
        className={classnames(
          'FormDropdown__option',
          {
            'FormDropdown__option--selected': selectedOption ? effectiveIsOptionEqual(option.value, selectedOption.value) : false,
          }
        )}
        onClick={handleSelect.bind(null, option.value)}
      >
        <div className="FormDropdown__option-content">
          <span className="FormDropdown__option-label">{option.label}</span>
          {option.description && (
            <span className="FormDropdown__option-description">{option.description}</span>
          )}
        </div>
      </li>
    )
  })

  const renderSearchInput = () => {
    return (
      <li className="FormDropdown__search">
        <input
          className="FormDropdown__search-input"
          type="text"
          value={filter}
          onChange={handleFilterChange}
          placeholder={searchInputPlaceholder}
          autoFocus
          onClick={stopPropagation}
        />
      </li>
    )
  }

  const renderClearOption = () => {
    return (
      <li
        className={classnames(
          'FormDropdown__option',
          'FormDropdown__option--clear',
          {
            'FormDropdown__option--selected': value === null,
          }
        )}
        onClick={resetSelect}
      >
        <span className="FormDropdown__option-label">{clearOptionLabel}</span>
      </li>
    )
  }

  const renderCreateOption = () => {
    return (
      <li
        className="FormDropdown__option FormDropdown__option--custom"
        onClick={handleCreateOption}
      >
        <span className="FormDropdown__option-label">
          {renderCreateOptionLabel ? renderCreateOptionLabel(trimmedFilter) : `Add “${trimmedFilter}”`}
        </span>
      </li>
    )
  }

  const renderEmptyLabel = () => {
    return (
      <li className="FormDropdown__empty">
        <span>{emptyLabel}</span>
      </li>
    )
  }

  const renderOpeningButton = () => {
    return (
      <button
        type="button"
        className="FormDropdown__trigger"
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span
          className={classnames(
            'FormDropdown__value',
            {
              'FormDropdown__value--placeholder': !displayLabel,
            }
          )}
        >
          {displayLabel || placeholder}
        </span>
        <ArrowDown className="FormDropdown__caret" />
      </button>
    )
  }

  const renderDropdownMenu = () => {
    return (
      <div className={classnames('FormDropdown__menu', menuClassName)}>
        <ul>
          {allowSearch && renderSearchInput()}
          {allowClear && renderClearOption()}
          {filteredOptionsElements}
          {shouldShowCreateOption && renderCreateOption()}
          {isEmpty && renderEmptyLabel()}
        </ul>
      </div>
    )
  }

  return (
    <div
      className={classnames(
        'FormDropdown',
        listenerClass,
        className,
        {
          'FormDropdown--open': open,
          'FormDropdown--disabled': disabled,
        }
      )}
    >
      {renderOpeningButton()}
      {open && renderDropdownMenu()}
    </div>
  )
}

export default FormDropdown
